import React, { useState, useEffect } from 'react';
import Header from '../../components/navigation/Header';
import GlobalFilterBar from '../../components/navigation/GlobalFilterBar';
import ComplianceAlertBanner from '../../components/navigation/ComplianceAlertBanner';
import DataRefreshIndicator from '../../components/navigation/DataRefreshIndicator';

import Icon from '../../components/AppIcon';
import contractService from '../../services/contractService';
import LegalDisclaimerBanner from '../../components/navigation/LegalDisclaimerBanner';
import ContractGeneratorPanel from './components/ContractGeneratorPanel';
import ClauseLibraryPanel from './components/ClauseLibraryPanel';
import AuditTrailPanel from './components/AuditTrailPanel';
import LegalAlertsPanel from './components/LegalAlertsPanel';
import ContractOverviewCard from './components/ContractOverviewCard';

const EmploymentContractsLegalComplianceCenter = () => {
  const [activeTab, setActiveTab] = useState('generator');
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedContract, setSelectedContract] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    loadContracts();
  }, [refreshTrigger]);

  const loadContracts = async () => {
    try {
      setLoading(true);
      const data = await contractService?.getAll();
      setContracts(data || []);
    } catch (err) {
      setError(err?.message || 'Failed to load contracts');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (filters) => {
    // Filter logic implementation
  };

  const handleContractGenerated = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const activeContracts = contracts?.filter(c => c?.contractStatus === 'active')?.length || 0;
  const pendingRenewals = contracts?.filter(c => {
    if (!c?.endDate) return false;
    const daysUntilExpiry = Math.floor((new Date(c?.endDate) - new Date()) / (1000 * 60 * 60 * 24));
    return daysUntilExpiry <= 90 && daysUntilExpiry > 0;
  })?.length || 0;
  const complianceScore = contracts?.length > 0 ? Math.round((activeContracts / contracts?.length) * 100) : 100;
  const legalAlertCount = 3; // Mock data for legal alerts

  const tabs = [
    { id: 'generator', label: 'Contract Generator', icon: 'FileText' },
    { id: 'clauses', label: 'Clause Library', icon: 'BookOpen' },
    { id: 'audit', label: 'Audit Trail', icon: 'History' },
    { id: 'alerts', label: 'Legal Alerts', icon: 'Bell' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
      <Header />
      <LegalDisclaimerBanner />
      <ComplianceAlertBanner />
      <DataRefreshIndicator />
      <GlobalFilterBar onFilterChange={handleFilterChange} />

      <main className="pt-48 pb-12 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-3 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg">
                <Icon name="FileCheck" className="h-8 w-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Employment Contracts & Legal Compliance Center</h1>
                <p className="text-gray-600 mt-1">Comprehensive contract management aligned with PNG Employment Act 1978</p>
              </div>
            </div>
          </div>

          {/* Dashboard Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-8">
            {/* Overview Cards - 4 columns */}
            <div className="lg:col-span-4 space-y-4">
              <ContractOverviewCard
                title="Active Contracts"
                value={activeContracts}
                icon="FileCheck"
                color="green"
                subtitle="Currently in force"
              />
              <ContractOverviewCard
                title="Pending Renewals"
                value={pendingRenewals}
                icon="Clock"
                color="amber"
                subtitle="Within 90 days"
              />
              <ContractOverviewCard
                title="Compliance Score"
                value={`${complianceScore}%`}
                icon="Shield"
                color="blue"
                subtitle="PNG Act adherence"
              />
              <ContractOverviewCard
                title="Legal Alerts"
                value={legalAlertCount}
                icon="Bell"
                color="red"
                subtitle="Regulatory updates"
              />
            </div>

            {/* Main Workspace - 8 columns */}
            <div className="lg:col-span-8">
              <div className="bg-white rounded-xl shadow-lg border border-gray-200">
                {/* Tab Navigation */}
                <div className="border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
                  <div className="flex overflow-x-auto">
                    {tabs?.map((tab) => (
                      <button
                        key={tab?.id}
                        onClick={() => setActiveTab(tab?.id)}
                        className={`flex items-center gap-2 px-6 py-4 font-medium transition-all whitespace-nowrap ${
                          activeTab === tab?.id
                            ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50' :'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                        }`}
                      >
                        <Icon name={tab?.icon} className="h-5 w-5" />
                        {tab?.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Tab Content */}
                <div className="p-6">
                  {loading ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                    </div>
                  ) : error ? (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
                      <Icon name="AlertCircle" className="h-5 w-5 inline mr-2" />
                      {error}
                    </div>
                  ) : (
                    <>
                      {activeTab === 'generator' && (
                        <ContractGeneratorPanel
                          onContractGenerated={handleContractGenerated}
                        />
                      )}
                      {activeTab === 'clauses' && <ClauseLibraryPanel />}
                      {activeTab === 'audit' && (
                        <AuditTrailPanel contracts={contracts} />
                      )}
                      {activeTab === 'alerts' && <LegalAlertsPanel />}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default EmploymentContractsLegalComplianceCenter;